﻿/*
 * Purpose: Compose Legal Guardian form layout from hook state and child controls.
 * Boundary: Layout only; no business logic; delegates state and actions to hook.
 * Invariants:
 * - Renders all fields in fixed order using responsive grid layouts.
 * - ASCII only; no Unicode in comments or strings.
 */

// Imports
import { useMemo, useState } from 'react';

import PhoneNumber from '../../contact/phonenumber';
import Otp from '../../contact/otp';
import ListofCheckboxes from '../../selection/listofcheckboxes';

import { IdField, AgeField, NameField } from './legalguardian-controls';
import { PhoneContext } from '../../contact/phonenumber';
import { OtpContext } from '../../contact/otp';
import { CheckboxContext } from '../../selection/listofcheckboxes';
import PrivacyPolicyModal from '../../consent/privacypolicy';

// Component LegalGuardianLayout:
// Stateless layout consuming useLegalGuardian hook output
export default function LegalGuardianLayout({ ...h }) {
    const [policyOpen, setPolicyOpen] = useState(false);
    // Rendering - structured layout with grouped form fields
    return (
        <div className="space-y-4">
            <h1 id='login-title' className='text-xl font-semibold'>
                Legal Guardian
            </h1>
            {/* ID and Age - arranged in a 3-column grid */}
            <div className="grid md:grid-cols-3 gap-4">
                <IdField
                    id="guardian-id"
                    value={h.form.idNumber}
                    onChange={(e) => h.setField('idNumber', e.target.value.replace(/\D/g, ''))}
                    className="md:col-span-2"
                />
                <AgeField value={h.age} className="md:col-span-1" />
            </div>
            {/* Name fields - first and last */}
            <div className="grid md:grid-cols-2 gap-4">
                <NameField
                    id="guardian-first"
                    label="First name"
                    value={h.form.firstName}
                    onChange={(e) => h.setField('firstName', e.target.value)}
                />
                <NameField
                    id="guardian-last"
                    label="Last name"
                    value={h.form.lastName}
                    onChange={(e) => h.setField('lastName', e.target.value)}
                />
            </div>
            <PhoneContext.Provider value={useMemo(() =>
                ({ setFullNumber: h.setCellNumber, setIsValid: h.setIsPhoneValid }),
                [h.setCellNumber, h.setIsPhoneValid])}>
                <PhoneNumber
                    enableCountrySelector={false}
                    enabledCountries={['ZA']}
                    defaultCountry="ZA"
                    showValidationLabel={false}
                />
            <CheckboxContext.Provider value={useMemo(() =>
                ({ setCheckboxState: h.setCheckboxesState }), [h.setCheckboxesState])}>
                <ListofCheckboxes
                    label="Registration Terms & Conditions"
                    options={[h.guardianTcText]}
                    showValidationLabel={false}
                />
            </CheckboxContext.Provider>
            {/* Guardian Tracking Consent -- explicit Yes/No */}
            {h.guardianTrackingConsentText && (
                <div className='space-y-2'>
                    <label className='block text-sm font-medium text-neutral-300'>Tracking Consent</label>
                    <div className='p-3 md:p-4 border border-neutral-800 rounded-lg bg-neutral-900'>
                        <p className='text-sm text-neutral-300 leading-relaxed'>{(() => {
                            const phrase = 'Read our Privacy Policy for full details';
                            const parts = h.guardianTrackingConsentText.split(phrase);
                            if (parts.length === 2) {
                                return <>{parts[0]}<button type='button' onClick={() => setPolicyOpen(true)} className='text-emerald-400 hover:text-emerald-300 underline cursor-pointer bg-transparent border-none p-0 font-inherit text-sm inline'>{phrase}</button>{parts[1]}</>;
                            }
                            return h.guardianTrackingConsentText;
                        })()}</p>
                    </div>
                    <div className='flex gap-3'>
                        <button
                            type='button'
                            onClick={() => h.setGuardianTrackingConsentChoice(true)}
                            className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition touch-manipulation ${
                                h.guardianTrackingConsentChoice === true
                                    ? 'bg-emerald-600 text-white ring-2 ring-emerald-400'
                                    : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-neutral-200'
                            }`}
                            aria-pressed={h.guardianTrackingConsentChoice === true}
                        >
                            Yes, I consent
                        </button>
                        <button
                            type='button'
                            onClick={() => h.setGuardianTrackingConsentChoice(false)}
                            className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition touch-manipulation ${
                                h.guardianTrackingConsentChoice === false
                                    ? 'bg-orange-600 text-white ring-2 ring-orange-400'
                                    : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-neutral-200'
                            }`}
                            aria-pressed={h.guardianTrackingConsentChoice === false}
                        >
                            No, I decline
                        </button>
                    </div>
                    <PrivacyPolicyModal open={policyOpen} onClose={() => setPolicyOpen(false)} />
                </div>
            )}
            <hr className='my-1 border-neutral-800' />
            </PhoneContext.Provider>
            <OtpContext.Provider value={useMemo(() =>
                ({ setOtpState: h.setOtpState }), [h.setOtpState])}>
                <Otp
                    auth={h.auth}
                    idNumber={h.form.idNumber}
                    cellNumber={h.cellNumber}
                    isProceedReadyOtp={h.isProceedReadyOtp}
                    otpLength={6}
                />
            </OtpContext.Provider>

        </div>
    );
}                            